import React, { FC, RefObject, useEffect, useState } from 'react';
import {
  getCurrentMonthDateTimeStart,
  getCurrentWeekSundayAsDate,
  getMonthDateTimeStart,
  getWeekSundayAsDate,
  isCurrentDateBetween,
} from '../../utils/utilities';
import { Box, Grid } from '@mui/material';
import MediumTypography from './MediumTypography';
import './DateRangeComponent.css';
import moment from 'moment';
import dayjs, { Dayjs } from 'dayjs';
import {
  DatePicker,
  DatePickerProps,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CustomTextField from './CustomTextField';
interface DateRange {
  startDate: Date;
  endDate: Date;
}

//viewType: 'timeGridDay' | 'timeGridWeek' | 'dayGridMonth'
interface DateRangeProp {
  viewType: string;
  onDateRangeChange: (startDate: Date, endDate?: Date) => void;
  dataRef: RefObject<{ startDate: Date | null; endDate: Date | null }>;
}

type CustomDatePickerProps = Omit<
  DatePickerProps<Dayjs>,
  'open' | 'onOpen' | 'onClose' | 'views' | 'openTo' | 'format'
> & {
  mode: string;
};
const CustomDatePicker = ({ mode, ...props }: CustomDatePickerProps) => {
  const [open, setOpen] = useState(false);
  return (
    <DatePicker
      views={
        mode === 'dayGridMonth' ? ['year', 'month'] : ['year', 'month', 'day']
      }
      openTo={mode === 'dayGridMonth' ? 'month' : 'day'}
      {...props}
      slots={{ ...props.slots, field: CustomTextField }}
      slotProps={{
        ...props.slotProps,
        field: { setOpen } as never,
      }}
      open={open}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      closeOnSelect={true}
    />
  );
};
const DateRangeComponent: FC<DateRangeProp> = (props) => {
  const { viewType, onDateRangeChange, dataRef } = props;
  const [dateRange, setDateRange] = useState<DateRange>();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const isWeekView = React.useCallback(() => {
    return viewType === 'timeGridWeek' || viewType === 'listWeek';
  }, [viewType]);

  const isMonthView = React.useCallback(() => {
    return viewType === 'dayGridMonth';
  }, [viewType]);

  const isDayView = React.useCallback(() => {
    return viewType === 'timeGridDay';
  }, [viewType]);

  const normalizeDateTime = React.useCallback((date: Date): Date => {
    const newDate = new Date(date);
    newDate.setHours(0);
    newDate.setMinutes(0);
    newDate.setSeconds(0);
    return newDate;
  }, []);

  const calculateStartDateForMonthView = React.useCallback(
    (refStartDate: Date, refEndDate: Date): Date => {
      const targetDate = isCurrentDateBetween(refStartDate, refEndDate)
        ? new Date()
        : refStartDate;
      return getMonthDateTimeStart(targetDate);
    },
    [],
  );

  const calculateStartDateForWeekView = React.useCallback(
    (refStartDate: Date): Date => {
      const currentDate = dayjs();
      const calendarDate = dayjs(refStartDate);
      const isSameMonth = currentDate.month() === calendarDate.month();
      if (isSameMonth) {
        return getWeekSundayAsDate(new Date());
      }
      return getWeekSundayAsDate(refStartDate);
    },
    [viewType],
  );

  const calculateStartDateForDayView = React.useCallback(
    (refStartDate: Date, refEndDate: Date): Date => {
      const momentDate1 = moment(refStartDate);
      const momentDate2 = moment(refEndDate);
      const diffDays = momentDate2.diff(momentDate1, 'days');

      if (diffDays < 6) {
        return isCurrentDateBetween(refStartDate, refEndDate)
          ? new Date()
          : refStartDate;
      }

      if (diffDays === 7) {
        const end = new Date(refStartDate);
        end.setDate(end.getDate() + 6);
        return isCurrentDateBetween(refStartDate, end)
          ? new Date()
          : refStartDate;
      }

      const end = new Date(refStartDate);
      end.setMonth(end.getMonth() + 1);
      end.setDate(end.getDate() - 1);
      return isCurrentDateBetween(refStartDate, end)
        ? new Date()
        : refStartDate;
    },
    [],
  );

  const getInitialStartDate = React.useCallback((): Date => {
    if (!dataRef.current?.startDate) {
      if (isMonthView()) return getCurrentMonthDateTimeStart();
      if (isWeekView()) return getCurrentWeekSundayAsDate();
      return new Date();
    }

    const refStartDate = dataRef.current.startDate;
    const refEndDate = dataRef.current.endDate;

    if (!refEndDate) return refStartDate;

    if (isMonthView()) {
      return calculateStartDateForMonthView(refStartDate, refEndDate);
    }

    if (isWeekView()) {
      return calculateStartDateForWeekView(refStartDate);
    }

    return calculateStartDateForDayView(refStartDate, refEndDate);
  }, [
    dataRef,
    isMonthView,
    isWeekView,
    calculateStartDateForMonthView,
    calculateStartDateForWeekView,
    calculateStartDateForDayView,
  ]);

  const calculateEndDate = React.useCallback(
    (startDate: Date): Date => {
      const endDate = new Date(startDate);

      if (isWeekView()) {
        endDate.setDate(endDate.getDate() + 6);
      } else if (isMonthView()) {
        endDate.setMonth(endDate.getMonth() + 1);
      } else {
        endDate.setDate(endDate.getDate() + 1);
      }

      return endDate;
    },
    [isWeekView, isMonthView],
  );

  const updateDateRangeState = React.useCallback(
    (startDate: Date, endDate: Date) => {
      setDateRange({ startDate, endDate });

      const endDateToPass = new Date(endDate);
      endDateToPass.setDate(endDateToPass.getDate() + 1);

      if (dataRef.current !== null) {
        dataRef.current.startDate = startDate;
        dataRef.current.endDate = endDateToPass;
      }

      setSelectedDate(startDate);
    },
    [dataRef],
  );

  useEffect(() => {
    const startDate = normalizeDateTime(getInitialStartDate());
    const endDate = calculateEndDate(startDate);
    updateDateRangeState(startDate, endDate);
  }, [
    viewType,
    normalizeDateTime,
    getInitialStartDate,
    calculateEndDate,
    updateDateRangeState,
  ]);

  const handlePrev = () => {
    if (!dateRange) return;

    const newStartDate = new Date(dateRange.startDate);
    const newEndDate = new Date(dateRange.endDate);

    if (isWeekView()) {
      newStartDate.setDate(newStartDate.getDate() - 7);
      newEndDate.setDate(newEndDate.getDate() - 7);
    } else if (isMonthView()) {
      newStartDate.setMonth(newStartDate.getMonth() - 1);
      newEndDate.setMonth(newEndDate.getMonth() - 1);
    } else {
      newStartDate.setDate(newStartDate.getDate() - 1);
      newEndDate.setDate(newEndDate.getDate() - 1);
    }

    setDateRange({ startDate: newStartDate, endDate: newEndDate });
    const endDateToPass = new Date(newEndDate);
    endDateToPass.setDate(endDateToPass.getDate() + 1);
    if (dataRef.current !== null) {
      dataRef.current.startDate = newStartDate;
      dataRef.current.endDate = endDateToPass;
    }
    onDateRangeChange(newStartDate, endDateToPass);
  };

  const handleNext = () => {
    if (!dateRange) return;
    const newStartDate = new Date(dateRange.startDate);
    const newEndDate = new Date(dateRange.endDate);

    if (isWeekView()) {
      newStartDate.setDate(newStartDate.getDate() + 7);
      newEndDate.setDate(newEndDate.getDate() + 7);
    } else if (isMonthView()) {
      newStartDate.setMonth(newStartDate.getMonth() + 1);
      newEndDate.setMonth(newEndDate.getMonth() + 1);
    } else {
      newStartDate.setDate(newStartDate.getDate() + 1);
      newEndDate.setDate(newEndDate.getDate() + 1);
    }
    setDateRange({ startDate: newStartDate, endDate: newEndDate });
    const endDateToPass = new Date(newEndDate);
    endDateToPass.setDate(endDateToPass.getDate() + 1);
    if (dataRef.current !== null) {
      dataRef.current.startDate = newStartDate;
      dataRef.current.endDate = endDateToPass;
    }
    onDateRangeChange(newStartDate, endDateToPass);
  };

  const formatDate = (date: Date): string => {
    if (isMonthView())
      return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    else
      return date.toLocaleString('en-US', {
        month: 'long',
        year: 'numeric',
        day: 'numeric',
      });
  };

  const getDates = () => {
    if (!dateRange) return;
    const startDate = dateRange.startDate;
    const endDate = dateRange.endDate;
    if (isDayView() || isMonthView()) return formatDate(startDate);
    else return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };
  const onDatePickerChange = (newDate: Dayjs | null) => {
    if (!newDate) return;
    const picked = newDate.toDate();
    let newStart = new Date(picked);
    let newEnd = new Date(picked);

    if (isMonthView()) {
      newStart = getMonthDateTimeStart(picked);
      newEnd = new Date(newStart);
      newEnd.setMonth(newEnd.getMonth() + 1);
      newEnd.setDate(newEnd.getDate() - 1);
      setSelectedDate(newStart);
    } else if (isWeekView()) {
      newStart = getWeekSundayAsDate(picked);
      newEnd = new Date(newStart);
      newEnd.setDate(newEnd.getDate() + 6);
      setSelectedDate(picked);
    } else {
      newStart.setHours(0, 0, 0, 0);
      newEnd = new Date(newStart);
      newEnd.setDate(newEnd.getDate() + 1);
      setSelectedDate(newStart);
    }

    setDateRange({ startDate: newStart, endDate: newEnd });
    const endDateToPass = new Date(newEnd);
    endDateToPass.setDate(endDateToPass.getDate() + 1);
    if (dataRef.current !== null) {
      dataRef.current.startDate = newStart;
      dataRef.current.endDate = endDateToPass;
    }
    onDateRangeChange(newStart, endDateToPass);
  };
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Grid container direction="row" className="toolbar">
        <Box onClick={handlePrev} className="previousdate"></Box>
        <Box className="middlelayer">
          <MediumTypography label={getDates()} />
          <CustomDatePicker
            value={selectedDate ? dayjs(selectedDate) : null}
            onChange={(newValue) => {
              if (!newValue) return;
              const newDateStr = newValue.format('YYYY-MM-DD');
              const oldDateStr = selectedDate
                ? dayjs(selectedDate).format('YYYY-MM-DD')
                : null;

              if (newDateStr !== oldDateStr) {
                onDatePickerChange(newValue);
              }
            }}
            mode={viewType}
          />
        </Box>
        <Box></Box>
        <Box className="nextdate" onClick={handleNext}></Box>
      </Grid>
    </LocalizationProvider>
  );
};

export default DateRangeComponent;
